import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useInView, useScroll, useTransform } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { SmartImg } from "../Utility/Smartimg";
import { BLOG_IMGS } from "../Utility/Media";
import { blogItems } from "../Data/Blogsdata";

const EASE = [0.76, 0, 0.24, 1];
const MotionLink = typeof motion.create === "function" ? motion.create(Link) : motion(Link);
const STORAGE_KEY = "helmax-blog-view";

// سطر العنوان بيطلع من ورا قناع (المراقبة على العنوان الأب مش على المقصوص)
const MaskLine = ({ children, delay = 0, show }) => (
  <span className="mask">
    <motion.span
      className="mask__inner"
      initial={false}
      animate={{ y: show ? 0 : "115%" }}
      transition={{ duration: 1.1, delay: show ? delay : 0, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

// كل كلمة بتنور مع السكرول (وبتطفي لو رجعت لفوق): نفس تأثير فقرة About
const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.16, 1]);
  return (
    <motion.span className="word" style={{ opacity }}>
      {children}
    </motion.span>
  );
};

// نص بيتكتب كلمة كلمة بحسب السكرول
const WordsScroll = ({ text, as: Tag = "p", className, offset = ["start 0.88", "end 0.6"] }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset });
  const words = text.split(" ");
  return (
    <Tag ref={ref} className={className}>
      {words.map((w, i) => (
        <React.Fragment key={`${w}-${i}`}>
          <Word progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]}>
            {w}
          </Word>{" "}
        </React.Fragment>
      ))}
    </Tag>
  );
};

// نفس التأثير بس بيشتغل أول ما النص يظهر (للنص اللي بيتغير مع الهوفر زي ملخص لوحة المعاينة)
const WordsAuto = ({ text, as: Tag = "p", className, delay = 0, step = 0.035 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  const words = text.split(" ");
  return (
    <Tag ref={ref} className={className}>
      {words.map((w, i) => (
        <React.Fragment key={`${w}-${i}`}>
          <motion.span
            className="word"
            initial={false}
            animate={{ opacity: inView ? 1 : 0.16 }}
            transition={{ duration: 0.5, delay: inView ? delay + i * step : 0 }}
          >
            {w}
          </motion.span>{" "}
        </React.Fragment>
      ))}
    </Tag>
  );
};

const ListIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
    <path d="M3 5h14M3 10h14M3 15h14" />
  </svg>
);
const CardsIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
    <rect x="3" y="3" width="6" height="6" rx="1.2" />
    <rect x="11" y="3" width="6" height="6" rx="1.2" />
    <rect x="3" y="11" width="6" height="6" rx="1.2" />
    <rect x="11" y="11" width="6" height="6" rx="1.2" />
  </svg>
);

const getInitialView = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "cards" ? "cards" : "list";
  } catch {
    return "list";
  }
};

export const MainBlogs = () => {
  const { t, lang, locale } = useLang();
  const items = useMemo(() => blogItems(t, locale), [t, locale]);
  const num = (v, min = 1) => v.toLocaleString(locale, { minimumIntegerDigits: min, useGrouping: false });

  const [view, setView] = useState(getInitialView); // "list" | "cards"
  const [active, setActive] = useState(0);
  const headRef = useRef(null);
  const headIn = useInView(headRef, { amount: 0.3 });
  const current = items[active];

  // اختيار المستخدم بيتحفظ في المتصفح
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch {}
  }, [view]);

  const views = [
    { id: "list", label: t.blogs.views.list, icon: <ListIcon /> },
    { id: "cards", label: t.blogs.views.cards, icon: <CardsIcon /> },
  ];

  return (
    <section className="blogs" id="blogs">
      <div className="blogs__head" ref={headRef}>
        <div>
          <p className="blogs__label">{t.blogs.label}</p>
          <h2 className="blogs__title">
            {t.blogs.title.map((line, i) => (
              <MaskLine key={line} delay={i * 0.14} show={headIn}>
                {line}
              </MaskLine>
            ))}
          </h2>
        </div>

        <div className="blogs__tools">
          <WordsScroll as="p" className="blogs__sub" text={t.blogs.sub} offset={["start 0.95", "end 0.7"]} />
          {/* اختيار طريقة العرض */}
          <div className="blogs__toggle" role="group" aria-label={t.blogs.viewLabel}>
            {views.map((v) => (
              <button
                key={v.id}
                type="button"
                className={view === v.id ? "is-on" : ""}
                aria-pressed={view === v.id}
                onClick={() => setView(v.id)}
              >
                {view === v.id && (
                  <motion.span
                    layoutId="blog-view-pill"
                    className="blogs__pill"
                    transition={{ duration: 0.5, ease: EASE }}
                  />
                )}
                {v.icon}
                <span>{v.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {view === "list" ? (
          <motion.div
            key="list"
            className="blogs__grid"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            {/* لوحة المعاينة: ثابتة، وبتكبّر الصورة لما تقف عليها بالماوس (ديسكتوب بس) */}
            <aside className="blogs__preview" aria-hidden="true">
              {items.map((a, i) => (
                <div key={a.idx} className={`bprev ${active === i ? "is-on" : ""}`}>
                  <SmartImg sources={BLOG_IMGS[i % BLOG_IMGS.length]} />
                </div>
              ))}
              <div className="blogs__shade" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={`${lang}-${active}`}
                  className="blogs__info"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45, ease: EASE }}
                >
                  <span className="blogs__chip">{current.category}</span>
                  <h3 className="blogs__info-title">{current.title}</h3>
                  <WordsAuto as="p" className="blogs__excerpt" text={current.excerpt} delay={0.1} step={0.02} />
                </motion.div>
              </AnimatePresence>
            </aside>

            <div className="blogs__right">
              <ol className="blogs__list">
                {items.map((a, i) => (
                  <motion.li
                    key={a.idx}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.35 }}
                    transition={{ duration: 0.9, delay: 0.05 * i, ease: EASE }}
                  >
                    {/* لما نعمل صفحات المقالات نبدّل href بالمسار الحقيقي */}
                    <Link
                      to="/blogs"
                      className={`brow ${active === i ? "is-active" : ""}`}
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                    >
                      <span className="brow__no">{num(i + 1, 2)}</span>
                      <span className="brow__thumb" aria-hidden="true">
                        <SmartImg sources={BLOG_IMGS[i % BLOG_IMGS.length]} />
                      </span>
                      <span className="brow__main">
                        <span className="brow__meta">
                          {a.category} · <time dateTime={a.date}>{a.dateLabel}</time>
                        </span>
                        <span className="brow__title">{a.title}</span>
                      </span>
                      <span className="brow__side">
                        {num(a.minutes)} {t.blogs.min}
                      </span>
                      <span className="brow__arrow" aria-hidden="true">
                        →
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ol>

              <Link to="/blogs" className="btn btn--ghost blogs__all">
                {t.blogs.all}
              </Link>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="cards"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            {/* عرض الكروت: صورة كبيرة وتحتها بيانات المقال */}
            <div className="blogs__cards">
              {items.map((a, i) => (
                <MotionLink
                  key={a.idx}
                  to="/blogs"
                  className="bcard"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.2 }}
                  transition={{ duration: 0.9, delay: 0.06 * (i % 2), ease: EASE }}
                >
                  <div className="bcard__img">
                    <SmartImg sources={BLOG_IMGS[i % BLOG_IMGS.length]} />
                    <span className="bcard__chip">{a.category}</span>
                  </div>
                  <p className="bcard__meta">
                    <time dateTime={a.date}>{a.dateLabel}</time> · {num(a.minutes)} {t.blogs.min}
                  </p>
                  <WordsScroll as="h3" className="bcard__title" text={a.title} offset={["start 0.92", "end 0.7"]} />
                  <WordsScroll as="p" className="bcard__excerpt" text={a.excerpt} offset={["start 0.92", "end 0.65"]} />
                  <span className="bcard__link">{t.blogs.read}</span>
                </MotionLink>
              ))}
            </div>

            <Link to="/blogs" className="btn btn--ghost blogs__all blogs__all--cards">
              {t.blogs.all}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};